import { defineStore } from "pinia";
import { ref } from "vue";
import { Dogspaces, Kennels } from "@busy-human/k9-library";
import { Temporal } from "temporal-polyfill";
import { increment, getDoc, updateDoc, setDoc } from "firebase/firestore";
import { CollectionEntity } from "@busy-human/gearbox";

// eslint-disable-next-line max-lines-per-function
export const useKennelCount = defineStore("kennelcounts", () => {
    const kennelCount = ref(new Map<string, Kennels.Model['kennelCount']>());
    const kennelMax = ref(new Map<string, Kennels.Model['kennelMax']>());

    const defaultKennelMax = ref<Kennels.Model['kennelCount'] | null>(null);

    class MiscDataCollectionClass extends CollectionEntity<Kennels.Model['kennelCount'], Record<string, never>> {
        relationships = {};
        defaults(): Record<Dogspaces.Sizes, number> {
            return {
                ...Kennels.Collection.defaults().kennelCount
            };
        }
    }

    const MiscDataCollection = new MiscDataCollectionClass({path: "misc-data"});

    async function fetchDefaults() {
        defaultKennelMax.value = await MiscDataCollection.fetchData("default-max").catch(() => null);
    }

    fetchDefaults();

    /** Returns a key that can be used to get the count of a kennel on a given day in kennelCount */
    function kennelCountKey(year: number, month: number, day: number) {
        return `${Temporal.PlainDate.from({day, month, year}).toString()}`;
    }

    async function getKennelCounts(countKey: string){
        const datestr = countKey;

        const eg = kennelCount.value.get(datestr);
        const eg2 = kennelMax.value.get(datestr);
        if(eg) return {count: eg, max: eg2};

        try {
            const res = await Kennels.Collection.fetchData(datestr);
            kennelCount.value.set(datestr, res.kennelCount);
            if(res.kennelMax) kennelMax.value.set(datestr, res.kennelMax);
            return {count: res.kennelCount, max: res.kennelMax};
        } catch (e) {
            kennelCount.value.set(datestr, Kennels.Collection.defaults().kennelCount);
            const tmp = defaultKennelMax.value ? {...defaultKennelMax.value} : Kennels.Collection.defaults().kennelCount;
            kennelMax.value.set(datestr, tmp);
            return {count: Kennels.Collection.defaults().kennelCount, max: tmp};
        }
    }

    /** Get countKey using kennelCountKey */
    async function getKennelCount(countKey: string, kennelSize: Dogspaces.Sizes) {
        const datestr = countKey;

        const eg = kennelCount.value.get(datestr);
        const eg2 = kennelMax.value.get(datestr);
        if(eg) return (eg2?.[kennelSize] ?? 0) - eg[kennelSize];

        try {
            const res = await Kennels.Collection.fetchData(datestr);
            kennelCount.value.set(datestr, res.kennelCount);
            if(res.kennelMax) kennelMax.value.set(datestr, res.kennelMax);
            return (res.kennelMax?.[kennelSize] ?? 0) - res.kennelCount[kennelSize];
        } catch (e) {
            kennelCount.value.set(datestr, Kennels.Collection.defaults().kennelCount);
            const tmp = defaultKennelMax.value ? {...defaultKennelMax.value} : Kennels.Collection.defaults().kennelCount;
            kennelMax.value.set(datestr, tmp);
            return tmp[kennelSize];
        }
    }

    type dateKey = {
        year: number,
        month: number,
        day: number
    }

    async function kennelSpaceAvailableDay(date: string | dateKey, kennel: Dogspaces.Sizes, neededCount = 1): Promise<boolean> {
        date = Temporal.PlainDate.from(date);
        const key = kennelCountKey(date.year, date.month, date.day);
        const count = await getKennelCount(key, kennel);
        return count >= neededCount;
    }

    async function kennelSpaceAvailable(from: string | dateKey, to: string | dateKey, kennel: Dogspaces.Sizes, count = 1) {
        const start = Temporal.PlainDate.from(from);
        const dur = start.until(to, {largestUnit: "days", smallestUnit: "days"});
        const dates: string[] = [];
        for(let i = 0; i <= dur.days; i+= 1) {
            const d = start.add({days: i}).toString();
            dates.push(d);
        }

        const results = await Promise.all(dates.map(async d => ({date: d, avail: await kennelSpaceAvailableDay(d, kennel, count)})));
        const isAvail = results.reduce((prev, buh) => prev && buh.avail, true);
        return {avail: isAvail, results};
    }
    async function addKennelDay(date: string | dateKey, amount: number, kennel: Dogspaces.Sizes | 'all') {

        date = Temporal.PlainDate.from(date);

        const key = kennelCountKey(date.year, date.month, date.day);

        amount = Number(amount);

        // const model: Partial<WithFieldValue<Kennels.Model>> = {
        //     date: date.toString(), kennelCount: { [kennel]: fieldval } as WithFieldValue<Kennels.Model['kennelCount']>
        // };


        const currentKennelCounts = await ensureDefaultKennelsKey(key);

        const changes = [];

        const sizes = kennel === 'all' ? Object.values(Dogspaces.Sizes) : [kennel];
        for(const size of sizes){
            changes.push(`kennelCount.${size}`);
            if(amount < 0 && (amount + currentKennelCounts[size]) < 0){
                changes.push(increment(-1 * currentKennelCounts[size]));
            }else{
                changes.push(increment(amount));
            }
        }

        await updateDoc(Kennels.Collection.docRef(key), changes[0], changes[1], ...changes.slice(2));

        kennelCount.value.delete(key);
        await getKennelCounts(key);
    }
    async function addKennel(from: string | dateKey, to: string | dateKey, kennel: Dogspaces.Sizes | 'all', amount = 1) {
        const start = Temporal.PlainDate.from(from);
        const dur = start.until(to, {largestUnit: "days", smallestUnit: "days"});
        const dates: string[] = [];
        for(let i = 0; i <= dur.days; i+= 1) {
            const d = start.add({days: i}).toString();
            dates.push(d);
        }

        amount = Number(amount);

        await ensureDefaultMax();

        await Promise.all(dates.map(d => addKennelDay(d, amount, kennel)));
    }

    async function setMaxForDay(date: string | dateKey, amount: number, kennel: Dogspaces.Sizes | 'all') {
        date = Temporal.PlainDate.from(date);

        const key = kennelCountKey(date.year, date.month, date.day);

        amount = Number(amount);

        // const model: Partial<WithFieldValue<Kennels.Model>> = {
        //     date: date.toString(), kennelCount: { [kennel]: fieldval } as WithFieldValue<Kennels.Model['kennelCount']>
        // };

        await ensureDefaultKennelsKey(key);

        const changes = [];

        const sizes = kennel === 'all' ? Object.values(Dogspaces.Sizes) : [kennel];
        for(const size of sizes){
            changes.push(`kennelMax.${size}`);
            changes.push(amount);
        }

        await updateDoc(Kennels.Collection.docRef(key), changes[0] as string, changes[1], ...changes.slice(2));


        await updateDoc(Kennels.Collection.docRef(key), `kennelMax.${kennel}`, amount);

        kennelCount.value.delete(key);
        await getKennelCounts(key);
    }

    async function setMax(from: string | dateKey, to: string | dateKey, kennel: Dogspaces.Sizes | 'all', amount: number){
        const start = Temporal.PlainDate.from(from);
        const dur = start.until(to, {largestUnit: "days", smallestUnit: "days"});
        const dates: string[] = [];
        for(let i = 0; i <= dur.days; i+= 1) {
            const d = start.add({days: i}).toString();
            dates.push(d);
        }

        amount = Number(amount);

        await ensureDefaultMax();

        await Promise.all(dates.map(d => setMaxForDay(d, amount, kennel)));
    }

    async function adjustKennelCounts(date: string | dateKey, counts: Kennels.Model['kennelCount'], maxes: Kennels.Model['kennelCount']) {
        const d = Temporal.PlainDate.from(date);
        const key = kennelCountKey(d.year, d.month, d.day);

        for(const blah in counts) {
            counts[blah as Dogspaces.Sizes] = Number(counts[blah as Dogspaces.Sizes]);
            maxes[blah as Dogspaces.Sizes] = Number(maxes[blah as Dogspaces.Sizes]);
        }

        await MiscDataCollection.docEntity("default-max").set(maxes);

        await Kennels.Collection.docEntity(key).set({
            date: d.toString(),
            kennelCount: counts,
            kennelMax: maxes
        });

        kennelCount.value.set(key, counts);
        kennelMax.value.set(key, maxes);
    }

    async function adjustKennelCountsBulk(from: string | dateKey, to: string | dateKey, counts: Kennels.Model['kennelCount'], maxes: Kennels.Model['kennelCount']) {
        const start = Temporal.PlainDate.from(from);
        const dur = start.until(to, {largestUnit: "days", smallestUnit: "days"});
        const dates: string[] = [];
        for(let i = 0; i <= dur.days; i+= 1) {
            const d = start.add({days: i}).toString();
            dates.push(d);
        }

        await MiscDataCollection.docEntity("default-max").set(maxes);

        const adjustCount = async (date: string | dateKey) => {
            const d = Temporal.PlainDate.from(date);
            const key = kennelCountKey(d.year, d.month, d.day);
            await Kennels.Collection.docEntity(key).set({
                date: d.toString(),
                kennelCount: counts,
                kennelMax: maxes
            });
    
            kennelCount.value.set(key, counts);
            kennelMax.value.set(key, maxes);
        };

        await Promise.all(dates.map(d => adjustCount(d)));
    }

    async function ensureDefaultMax(){
        if(!defaultKennelMax.value) {
            try {
                // eslint-disable-next-line require-atomic-updates
                defaultKennelMax.value = await MiscDataCollection.fetchData("default-max");
            } catch (e) {
                console.error(e);
                throw new Error("Default max kennels not set, please set the max kennels on any day first.");
            }
        }
    }

    async function ensureDefaultKennels(date: string | dateKey){
        const d = Temporal.PlainDate.from(date);
        const key = kennelCountKey(d.year, d.month, d.day);
        return ensureDefaultKennelsKey(key);
    }

    async function ensureDefaultKennelsKey(key: string){
        let kennelCounts;
        const t = await getDoc(Kennels.Collection.docRef(key));
        if(!t.exists()) {
            await setDoc(Kennels.Collection.docRef(key), {
                ...Kennels.Collection.defaults(),
                kennelMax: defaultKennelMax.value || Kennels.Collection.defaults().kennelCount
            });
            kennelCounts = Kennels.Collection.defaults().kennelCount;
        }else{
            kennelCounts = t.data().kennelCount;
        }

        return kennelCounts;
    }

    return { kennelCount, kennelMax, defaultKennelMax, fetchDefaults, kennelSpaceAvailable, getKennelCount, kennelCountKey, addKennel, adjustKennelCounts, adjustKennelCountsBulk, setMax };
});